import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeftIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateWebhook } from '@/components/features/webhooks/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { EventSelector } from '@/components/webhooks/event-selector';
import { WebhookSecretDialog } from '@/components/webhooks/webhook-secret-dialog';
import type { Webhook } from '@/lib/api-client';

function CreateWebhookPage() {
  const { t } = useTranslation('integrations');
  const navigate = useNavigate();
  const createWebhook = useCreateWebhook();

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    events: [] as string[],
    maxAttempts: 3,
    initialDelay: 60,
    multiplier: 2,
    retentionDays: 7,
    isActive: true,
  });

  // State for secret dialog
  const [secretDialog, setSecretDialog] = useState({
    isOpen: false,
    secret: '',
    webhookName: '',
    webhookId: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    createWebhook.mutate(
      {
        name: formData.name,
        url: formData.url,
        events: formData.events,
        retryPolicy: {
          maxAttempts: formData.maxAttempts,
          initialDelay: formData.initialDelay,
          multiplier: formData.multiplier,
        },
        retentionDays: formData.retentionDays,
        isActive: formData.isActive,
      },
      {
        onSuccess: (result) => {
          // Show secret dialog if secret is present
          const webhook = result as Webhook & { secret?: string };
          if (webhook.secret) {
            setSecretDialog({
              isOpen: true,
              secret: webhook.secret,
              webhookName: webhook.name,
              webhookId: webhook.id,
            });
          } else {
            navigate({ to: '/integrations/webhooks' });
          }
        },
      },
    );
  };

  return (
    <>
      <div className="flex flex-col gap-4 max-w-2xl">
        <div className="flex items-center gap-4">
          <Link to="/integrations/webhooks">
            <Button variant="ghost" size="icon">
              <ArrowLeftIcon className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('webhooks.create')}</h1>
            <p className="text-sm text-muted-foreground">Configure a new webhook endpoint</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">{t('webhooks.form.name')}</Label>
            <Input
              id="name"
              placeholder={t('webhooks.form.namePlaceholder')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="url">{t('webhooks.form.url')}</Label>
            <Input
              id="url"
              type="url"
              placeholder={t('webhooks.form.urlPlaceholder')}
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              required
            />
            <p className="text-xs text-muted-foreground">{t('webhooks.form.urlHelp')}</p>
          </div>

          <div className="space-y-2">
            <Label>{t('webhooks.form.events')}</Label>
            <EventSelector
              value={formData.events}
              onChange={(events) => setFormData({ ...formData, events })}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="maxAttempts">{t('webhooks.form.maxAttempts')}</Label>
              <Input
                id="maxAttempts"
                type="number"
                min="1"
                max="10"
                value={formData.maxAttempts}
                onChange={(e) =>
                  setFormData({ ...formData, maxAttempts: parseInt(e.target.value, 10) })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="initialDelay">{t('webhooks.form.initialDelay')}</Label>
              <Input
                id="initialDelay"
                type="number"
                min="1"
                value={formData.initialDelay}
                onChange={(e) =>
                  setFormData({ ...formData, initialDelay: parseInt(e.target.value, 10) })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="multiplier">{t('webhooks.form.backoffMultiplier')}</Label>
              <Input
                id="multiplier"
                type="number"
                min="1"
                step="0.1"
                value={formData.multiplier}
                onChange={(e) =>
                  setFormData({ ...formData, multiplier: parseFloat(e.target.value) })
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="retentionDays">{t('webhooks.form.retentionDays')}</Label>
            <Input
              id="retentionDays"
              type="number"
              min="1"
              max="90"
              value={formData.retentionDays}
              onChange={(e) =>
                setFormData({ ...formData, retentionDays: parseInt(e.target.value, 10) })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="isActive">{t('webhooks.form.isActive')}</Label>
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) => setFormData({ ...formData, isActive: !!checked })}
            />
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={createWebhook.isPending}>
              {createWebhook.isPending ? 'Creating...' : 'Create Webhook'}
            </Button>
            <Link to="/integrations/webhooks">
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
          </div>
        </form>
      </div>

      {/* Secret Dialog */}
      <WebhookSecretDialog
        isOpen={secretDialog.isOpen}
        secret={secretDialog.secret}
        webhookName={secretDialog.webhookName}
        webhookId={secretDialog.webhookId}
        onClose={() => setSecretDialog({ ...secretDialog, isOpen: false })}
      />
    </>
  );
}

export const Route = createFileRoute('/_authenticated/integrations/webhooks/create')({
  component: CreateWebhookPage,
});
